import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  Dialog,
  DialogContent,
  InputAdornment,
  MenuItem,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaVenusMars,
  FaShieldAlt,
  FaCamera,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaBriefcase,
  FaBirthdayCake,
} from "react-icons/fa";
import { MdSave } from "react-icons/md";

const EASE = [0.22, 1, 0.36, 1];

/* ── Tabs config ───────────────────────────── */
const TABS = [
  { id: "profile", label: "Profile" },
  { id: "contact", label: "Contact" },
  { id: "details", label: "Details" },
  { id: "settings", label: "Settings" },
];

/* ── Shared styles ─────────────────────────── */
const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "0.67rem",
      fontWeight: 700,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.09em",
      mb: 0.55,
      display: "flex",
      alignItems: "center",
      gap: 0.4,
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: "#f43f5e", fontSize: "0.65rem" }}>
        *
      </Box>
    )}
  </Typography>
);

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    fontSize: "0.84rem",
    color: "#0f172a",
    background: "#f8fafc",
    transition: "all 0.18s",
    "& fieldset": { borderColor: "#edf0f7" },
    "&:hover fieldset": { borderColor: "#93c5fd" },
    "&.Mui-focused fieldset": { borderColor: "#0288D1", borderWidth: 1.5 },
    "&.Mui-focused": {
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(2,136,209,0.08)",
    },
  },
  "& input::placeholder, & textarea::placeholder": {
    color: "#c8d3df",
    opacity: 1,
    fontSize: "0.82rem",
  },
  "& .MuiSelect-select": { fontSize: "0.84rem" },
};

const adorn = (active) => (active ? "#0288D1" : "#c8d3df");

/* ── Tab content panels ────────────────────── */

const ProfileTab = ({ teacher, setTeacher, saving, focused, setFocused }) => (
  <Stack spacing={2}>
    <Box>
      <FieldLabel required>Full Name</FieldLabel>
      <TextField
        fullWidth
        placeholder="e.g. Amara Johnson"
        value={teacher.tempName || ""}
        onChange={(e) => setTeacher({ ...teacher, tempName: e.target.value })}
        onFocus={() => setFocused("name")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaUser size={12} color={adorn(focused === "name")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Stack direction="row" spacing={1.5}>
      <Box sx={{ flex: 1 }}>
        <FieldLabel>Date of Birth</FieldLabel>
        <TextField
          fullWidth
          type="date"
          value={teacher.tempDate_of_birth || ""}
          onChange={(e) =>
            setTeacher({ ...teacher, tempDate_of_birth: e.target.value })
          }
          disabled={saving}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaBirthdayCake size={12} color="#c8d3df" />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          sx={inputSx}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <FieldLabel>Gender</FieldLabel>
        <TextField
          select
          fullWidth
          value={teacher.tempGender || ""}
          onChange={(e) =>
            setTeacher({ ...teacher, tempGender: e.target.value })
          }
          disabled={saving}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaVenusMars size={12} color="#c8d3df" />
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        >
          <MenuItem value="">Select gender</MenuItem>
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Female">Female</MenuItem>
        </TextField>
      </Box>
    </Stack>
  </Stack>
);

const ContactTab = ({ teacher, setTeacher, saving, focused, setFocused }) => (
  <Stack spacing={2}>
    <Box>
      <FieldLabel>Email Address</FieldLabel>
      <TextField
        fullWidth
        placeholder="teacher@school.edu"
        value={teacher.tempEmail || ""}
        onChange={(e) => setTeacher({ ...teacher, tempEmail: e.target.value })}
        onFocus={() => setFocused("email")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaEnvelope size={12} color={adorn(focused === "email")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Box>
      <FieldLabel>Phone Number</FieldLabel>
      <TextField
        fullWidth
        placeholder="+234 800 000 0000"
        value={teacher.tempPhone || ""}
        onChange={(e) => setTeacher({ ...teacher, tempPhone: e.target.value })}
        onFocus={() => setFocused("phone")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaPhone size={12} color={adorn(focused === "phone")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Box>
      <FieldLabel>Address</FieldLabel>
      <TextField
        fullWidth
        multiline
        minRows={2}
        placeholder="Street address, city, state…"
        value={teacher.tempAddress || ""}
        onChange={(e) =>
          setTeacher({ ...teacher, tempAddress: e.target.value })
        }
        onFocus={() => setFocused("address")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ alignSelf: "flex-start", mt: 1.2 }}
            >
              <FaMapMarkerAlt size={12} color={adorn(focused === "address")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>
  </Stack>
);

const DetailsTab = ({ teacher, setTeacher, saving, focused, setFocused }) => (
  <Stack spacing={2}>
    <Box>
      <FieldLabel>Qualification</FieldLabel>
      <TextField
        fullWidth
        placeholder="e.g. B.Sc. Education, M.A. Mathematics…"
        value={teacher.tempQualification || ""}
        onChange={(e) =>
          setTeacher({ ...teacher, tempQualification: e.target.value })
        }
        onFocus={() => setFocused("qual")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaGraduationCap size={13} color={adorn(focused === "qual")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Box>
      <FieldLabel>Years of Experience</FieldLabel>
      <TextField
        fullWidth
        type="number"
        placeholder="0"
        value={teacher.tempYears_of_experience ?? ""}
        onChange={(e) =>
          setTeacher({
            ...teacher,
            tempYears_of_experience:
              e.target.value === "" ? "" : Number(e.target.value),
          })
        }
        onFocus={() => setFocused("exp")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        inputProps={{ min: 0, max: 60 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaBriefcase size={12} color={adorn(focused === "exp")} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                yrs
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    {/* Experience visual bar */}
    {teacher.tempYears_of_experience > 0 && (
      <Box sx={{ mt: -0.5 }}>
        <Box
          sx={{
            height: 3,
            borderRadius: 99,
            background: "#f1f5f9",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.min(
                (teacher.tempYears_of_experience / 40) * 100,
                100
              )}%`,
              background: "linear-gradient(90deg, #0288D1, #38bdf8)",
              borderRadius: 99,
              transition: "width 0.4s ease",
            }}
          />
        </Box>
        <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", mt: 0.4 }}>
          {teacher.tempYears_of_experience >= 20
            ? "Senior educator"
            : teacher.tempYears_of_experience >= 10
            ? "Experienced educator"
            : teacher.tempYears_of_experience >= 5
            ? "Mid-level educator"
            : "Early career"}
        </Typography>
      </Box>
    )}
  </Stack>
);

const SettingsTab = ({ teacher, setTeacher, saving }) => {
  const isAdmin = teacher.tempIsClassAdmin;
  return (
    <Stack spacing={2.5}>
      <Box
        onClick={() =>
          !saving &&
          setTeacher({
            ...teacher,
            tempIsClassAdmin: !teacher.tempIsClassAdmin,
          })
        }
        sx={{
          px: 2,
          py: 1.4,
          borderRadius: 2,
          background: isAdmin ? "rgba(2,136,209,0.04)" : "#f8fafc",
          border: `1.5px solid ${isAdmin ? "rgba(2,136,209,0.2)" : "#edf0f7"}`,
          cursor: saving ? "default" : "pointer",
          transition: "all 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.8,
              background: isAdmin
                ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                : "#e8edf5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.22s",
              boxShadow: isAdmin ? "0 3px 10px rgba(2,136,209,0.3)" : "none",
              flexShrink: 0,
            }}
          >
            <FaShieldAlt size={13} color={isAdmin ? "#fff" : "#94a3b8"} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#0f172a",
                fontSize: "0.84rem",
                lineHeight: 1.2,
              }}
            >
              Class Admin
            </Typography>
            <Typography sx={{ fontSize: "0.69rem", color: "#94a3b8" }}>
              Can manage classes and students
            </Typography>
          </Box>
        </Stack>
        <Switch
          checked={!!isAdmin}
          disabled={saving}
          onClick={(e) => e.stopPropagation()}
          onChange={() =>
            setTeacher({
              ...teacher,
              tempIsClassAdmin: !teacher.tempIsClassAdmin,
            })
          }
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#0288D1" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              background: "#0288D1",
              opacity: 1,
            },
            "& .MuiSwitch-track": { background: "#c8d3df", opacity: 1 },
          }}
        />
      </Box>

      {/* Info notice */}
      <Box
        sx={{
          px: 2,
          py: 1.2,
          borderRadius: 2,
          background: "rgba(251,191,36,0.06)",
          border: "1px solid rgba(251,191,36,0.18)",
        }}
      >
        <Typography
          sx={{ fontSize: "0.71rem", color: "#92400e", lineHeight: 1.55 }}
        >
          Changing permissions will take effect immediately after saving. The
          teacher will be notified of any role changes.
        </Typography>
      </Box>
    </Stack>
  );
};

/* ── Main modal ────────────────────────────── */
export default function EditTeacherModal({
  open,
  onClose,
  teacher,
  setTeacher,
  onSave,
}) {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [focused, setFocused] = useState(null);

  if (!teacher) return null;

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  const avatarSrc = teacher.tempNewPassport
    ? URL.createObjectURL(teacher.tempNewPassport)
    : teacher.tempPassport || null;

  const initials =
    teacher.tempName
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "T";
  
  console.log("Current Teacher State:", teacher);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(12px)",
          background: "rgba(8,16,36,0.6)",
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <DialogContent sx={{ p: 0, overflow: "visible" }}>
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "22px",
                  border: "1px solid #e4eaf4",
                  boxShadow:
                    "0 40px 100px rgba(2,50,120,0.16), 0 8px 24px rgba(0,0,0,0.07)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* ── Banner ── */}
                <Box
                  sx={{
                    height: 96,
                    background:
                      "linear-gradient(135deg, #09122c 0%, #0d2448 45%, #0b3565 100%)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Subtle pattern rings */}
                  {[
                    { size: 120, top: -40, left: -20, opacity: 0.05 },
                    { size: 80, top: 10, right: 40, opacity: 0.06 },
                    { size: 60, bottom: -20, right: 100, opacity: 0.07 },
                    { size: 44, top: 8, left: 80, opacity: 0.05 },
                  ].map((r, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        top: r.top,
                        left: r.left,
                        right: r.right,
                        bottom: r.bottom,
                        width: r.size,
                        height: r.size,
                        borderRadius: "50%",
                        border: `1px solid rgba(255,255,255,${
                          r.opacity * 2.5
                        })`,
                        background: `radial-gradient(circle, rgba(255,255,255,${r.opacity}) 0%, transparent 70%)`,
                      }}
                    />
                  ))}

                  {/* Name + meta in banner */}
                  <Box sx={{ position: "absolute", bottom: 12, left: 104 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 800,
                        color: "rgba(255,255,255,0.95)",
                        textTransform: "capitalize",
                        lineHeight: 1.2,
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {teacher.tempName || "Teacher"}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.8}
                      sx={{ mt: 0.3 }}
                    >
                      <Box
                        sx={{
                          px: 0.9,
                          py: 0.15,
                          borderRadius: 99,
                          background: teacher.tempIsClassAdmin
                            ? "rgba(14,165,233,0.25)"
                            : "rgba(255,255,255,0.1)",
                          border: `1px solid ${
                            teacher.tempIsClassAdmin
                              ? "rgba(14,165,233,0.4)"
                              : "rgba(255,255,255,0.15)"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            color: teacher.tempIsClassAdmin
                              ? "#7dd3fc"
                              : "rgba(255,255,255,0.5)",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                          }}
                        >
                          {teacher.tempIsClassAdmin ? "Class Admin" : "Teacher"}
                        </Typography>
                      </Box>
                      {teacher.code && (
                        <Typography
                          sx={{
                            fontSize: "0.62rem",
                            color: "rgba(255,255,255,0.35)",
                            fontFamily: "monospace",
                          }}
                        >
                          · {teacher.code}
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {/* Close button */}
                  <IconButton
                    size="small"
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      color: "rgba(255,255,255,0.45)",
                      width: 28,
                      height: 28,
                      transition: "all 0.18s",
                      "&:hover": {
                        background: "rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    <FaTimes size={11} />
                  </IconButton>
                </Box>

                {/* ── Avatar overlap ── */}
                <Box
                  sx={{
                    px: 3,
                    mt: "-32px",
                    mb: 0,
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={avatarSrc}
                      sx={{
                        width: 64,
                        height: 64,
                        fontSize: "1.15rem",
                        fontWeight: 800,
                        background: avatarSrc
                          ? "transparent"
                          : "linear-gradient(135deg, #0288D1, #38bdf8)",
                        border: "3.5px solid #fff",
                        boxShadow: "0 4px 18px rgba(2,136,209,0.28)",
                        color: "#fff",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {!avatarSrc && initials}
                    </Avatar>

                    {/* Camera hover overlay */}
                    <Tooltip title="Change photo" placement="top" arrow>
                      <Box
                        component="label"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          background: "transparent",
                          transition: "background 0.2s",
                          "& .cam-icon": {
                            opacity: 0,
                            transition: "opacity 0.2s",
                          },
                          "&:hover": { background: "rgba(0,0,0,0.4)" },
                          "&:hover .cam-icon": { opacity: 1 },
                        }}
                      >
                        <FaCamera className="cam-icon" size={14} color="#fff" />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) =>
                            setTeacher({
                              ...teacher,
                              tempNewPassport: e.target.files[0],
                            })
                          }
                        />
                      </Box>
                    </Tooltip>

                    {/* Status dot */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 3,
                        right: 3,
                        width: 13,
                        height: 13,
                        borderRadius: "50%",
                        background: "#22c55e",
                        border: "2.5px solid #fff",
                        boxShadow: "0 0 0 2px rgba(34,197,94,0.2)",
                      }}
                    />
                  </Box>

                  {/* Experience badge */}
                  {teacher.tempYearsOfExperience > 0 && (
                    <Box
                      sx={{
                        mb: 0.5,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 99,
                        background: "rgba(2,136,209,0.07)",
                        border: "1px solid rgba(2,136,209,0.15)",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <FaBriefcase size={9} color="#0288D1" />
                      <Typography
                        sx={{
                          fontSize: "0.67rem",
                          fontWeight: 700,
                          color: "#0288D1",
                        }}
                      >
                        {teacher.tempYearsOfExperience} yr
                        {teacher.tempYearsOfExperience !== 1 ? "s" : ""} exp.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* ── Tab bar ── */}
                <Box sx={{ px: 3, pt: 1.8, pb: 0 }}>
                  <Stack
                    direction="row"
                    sx={{
                      borderBottom: "1px solid #f0f4fa",
                      gap: 0,
                    }}
                  >
                    {TABS.map((tab) => {
                      const active = activeTab === tab.id;
                      return (
                        <Box
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          sx={{
                            px: 1.6,
                            py: 0.9,
                            cursor: "pointer",
                            position: "relative",
                            transition: "all 0.16s",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: active ? 700 : 500,
                              color: active ? "#0288D1" : "#94a3b8",
                              transition: "all 0.16s",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tab.label}
                          </Typography>
                          {active && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: -1,
                                left: 0,
                                right: 0,
                                height: 2,
                                borderRadius: "2px 2px 0 0",
                                background:
                                  "linear-gradient(90deg, #0288D1, #38bdf8)",
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>

                {/* ── Tab content ── */}
                <Box sx={{ px: 3, pt: 2.5, pb: 3, minHeight: 220 }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -8 }}
                      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    >
                      {activeTab === "profile" && (
                        <ProfileTab
                          teacher={teacher}
                          setTeacher={setTeacher}
                          saving={saving}
                          focused={focused}
                          setFocused={setFocused}
                        />
                      )}
                      {activeTab === "contact" && (
                        <ContactTab
                          teacher={teacher}
                          setTeacher={setTeacher}
                          saving={saving}
                          focused={focused}
                          setFocused={setFocused}
                        />
                      )}
                      {activeTab === "details" && (
                        <DetailsTab
                          teacher={teacher}
                          setTeacher={setTeacher}
                          saving={saving}
                          focused={focused}
                          setFocused={setFocused}
                        />
                      )}
                      {activeTab === "settings" && (
                        <SettingsTab
                          teacher={teacher}
                          setTeacher={setTeacher}
                          saving={saving}
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </Box>

                {/* ── Footer / Actions ── */}
                <Box
                  sx={{
                    px: 3,
                    pb: 3,
                    pt: 0,
                    borderTop: "1px solid #f0f4fa",
                  }}
                >
                  <Stack direction="row" spacing={1.2} sx={{ pt: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={onClose}
                      disabled={saving}
                      sx={{
                        py: 1.15,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        borderColor: "#e4eaf4",
                        color: "#64748b",
                        "&:hover": {
                          borderColor: "#94a3b8",
                          background: "#f8fafc",
                          color: "#475569",
                        },
                      }}
                    >
                      Discard
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving || !teacher.tempName?.trim()}
                      startIcon={saving ? null : <MdSave size={13} />}
                      sx={{
                        py: 1.15,
                        borderRadius: 2,
                        fontWeight: 800,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        background:
                          "linear-gradient(135deg, #09122c 0%, #0d3060 100%)",
                        boxShadow: "0 4px 16px rgba(9,18,44,0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #0d1e42 0%, #0f3d78 100%)",
                          boxShadow: "0 6px 20px rgba(9,18,44,0.4)",
                        },
                        "&:disabled": {
                          background: "#e4eaf4",
                          color: "#94a3b8",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {saving ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={13} sx={{ color: "#fff" }} />
                          <span>Saving…</span>
                        </Stack>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </DialogContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
